import axios from '@/utils/axios';
// types
import {
  ClientCleanerRestrictionDto,
  ClientAdminReminder,
  ClientAdminReminderDto,
  ClientPaymentHoldRule,
  ClientPaymentHoldRuleDto,
  ClientLinkedAccountsResult,
  LinkClientAccountsResult,
  UnlinkClientAccountResult,
  UpdateOneSignalEmailSubscriptionDto,
  UpdateOneSignalEmailSubscriptionResult,
  UpdateClientCleanerRestrictionsResult,
  DashboardUserDuoStatus,
  User,
  UserDto,
  UserOneSignalEmailSubscription,
  UserWithUpdatedRoles,
} from '@/features/users/shared/types/users';
import { Booking } from '@/features/bookings-management/types';
import type {
  GetTransactionsParams,
  TransactionsListResponse,
} from '@/features/transactions/api';
import type { ProviderJob } from '@/features/users/providers/types';
import { ProviderTimeOff } from '../types';

type ClientPaymentHoldRuleResponse =
  | ClientPaymentHoldRule
  | { payment_hold_rule?: ClientPaymentHoldRule | null }
  | null
  | undefined;

const hasPaymentHoldRuleProperty = (
  data: ClientPaymentHoldRuleResponse
): data is { payment_hold_rule?: ClientPaymentHoldRule | null } =>
  typeof data === 'object' && data !== null && 'payment_hold_rule' in data;

const normalizeClientPaymentHoldRule = (
  data: ClientPaymentHoldRuleResponse
): ClientPaymentHoldRule | null => {
  if (!data) return null;

  if (hasPaymentHoldRuleProperty(data)) return data.payment_hold_rule ?? null;

  return data;
};

// GET
export async function getUsersByType(type: number): Promise<User[]> {
  const {
    data: { data },
  } = await axios.get(`/users/bytype/${type}`);
  return data;
}
export async function getDashboardUsers(): Promise<User[]> {
  const {
    data: { data },
  } = await axios.get('/users/bytype/3');
  return data;
}
export async function getActiveDashboardUsers(): Promise<User[]> {
  const {
    data: { data },
  } = await axios.get('/users/bytype/3/bystatus/1');
  return data;
}
export async function getProviders(statusId?: number): Promise<User[]> {
  const endpoint =
    typeof statusId === 'number'
      ? `/users/bytype/2/bystatus/${statusId}`
      : '/users/bytype/2';
  const {
    data: { data },
  } = await axios.get(endpoint);
  return data;
}

export async function getActiveProviders(): Promise<User[]> {
  const {
    data: { data },
  } = await axios.get('/users/bytype/2/bystatus/1');
  return data;
}

export async function getProvidersTimeOff(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<ProviderTimeOff[]> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) {
    searchParams.append('startDate', params.startDate);
  }
  if (params?.endDate) {
    searchParams.append('endDate', params.endDate);
  }

  const queryString = searchParams.toString();
  const endpoint = `/users/providers/time-off${
    queryString ? `?${queryString}` : ''
  }`;

  const {
    data: { data },
  } = await axios.get(endpoint);
  return data;
}

export async function getClients(statusId: number = 1): Promise<User[]> {
  const endpoint = `/users/bytype/1/bystatus/${statusId}`;
  const {
    data: { data },
  } = await axios.get(endpoint);
  return data;
}

export async function getSingleUser(id: number): Promise<User> {
  const {
    data: { data },
  } = await axios.get(`/users/${id}`);

  return data;
}

export async function getClientLinkedAccounts(
  clientId: number | string
): Promise<ClientLinkedAccountsResult> {
  const {
    data: { data },
  } = await axios.get(`/users/${clientId}/linked-accounts`);

  return data;
}

export async function getClientAdminReminders(
  clientId: number | string
): Promise<ClientAdminReminder[]> {
  const {
    data: { data },
  } = await axios.get(`/users/${clientId}/admin-reminders`);
  const remindersPayload = data as
    | ClientAdminReminder[]
    | { admin_reminders?: ClientAdminReminder[] };

  if (Array.isArray(remindersPayload)) return remindersPayload;

  return Array.isArray(remindersPayload?.admin_reminders)
    ? remindersPayload.admin_reminders
    : [];
}

export async function getClientPaymentHoldRule(
  clientId: number | string
): Promise<ClientPaymentHoldRule | null> {
  const {
    data: { data },
  } = await axios.get(`/users/${clientId}/payment-hold-rule`);

  return normalizeClientPaymentHoldRule(data);
}

export async function getUserOneSignalEmailSubscription(
  userId: number
): Promise<UserOneSignalEmailSubscription> {
  const {
    data: { data },
  } = await axios.get(`/users/${userId}/onesignal/subscriptions/email`);

  return {
    user_id: data.user_id,
    enabled: typeof data.enabled === 'boolean' ? data.enabled : null,
    source: 'onesignal',
    subscription: data.subscription
      ? {
          id: data.subscription.id ?? null,
          token: data.subscription.token ?? null,
          enabled:
            typeof data.subscription.enabled === 'boolean'
              ? data.subscription.enabled
              : null,
          notification_types:
            typeof data.subscription.notification_types === 'number'
              ? data.subscription.notification_types
              : null,
        }
      : null,
  };
}

export async function getDashboardUserDuoStatus(
  userId: number
): Promise<DashboardUserDuoStatus> {
  const {
    data: { data },
  } = await axios.get(`/admin/duo/dashboard-users/${userId}/status`);

  return data;
}

// POST
export async function createNewUser(userData: UserDto): Promise<User> {
  const createUserData = { ...userData };
  delete createUserData.is_chat_user;
  const {
    data: { data },
  } = await axios.post(`/users`, createUserData);

  return data;
}

export async function sendDashboardUserDuoInvite(
  userId: number
): Promise<DashboardUserDuoStatus> {
  const {
    data: { data },
  } = await axios.post(`/admin/duo/dashboard-users/${userId}/invite`);

  return data;
}

export async function createClientAdminReminder(
  clientId: number | string,
  payload: ClientAdminReminderDto
): Promise<ClientAdminReminder> {
  const {
    data: { data },
  } = await axios.post(`/users/${clientId}/admin-reminders`, payload);

  return data;
}

export async function linkClientAccounts(
  clientId: number | string,
  payload: { user_ids: number[] }
): Promise<LinkClientAccountsResult> {
  const {
    data: { data },
  } = await axios.post(`/users/${clientId}/linked-accounts`, payload);

  return data;
}

// PUT
export const updateUser = async (userID: number, userData: UserDto) =>
  axios.put(`/users/${userID}`, userData);

export async function updateClientCleanerRestrictions(
  clientId: number,
  cleanerRestrictions: ClientCleanerRestrictionDto[]
): Promise<UpdateClientCleanerRestrictionsResult> {
  const {
    data: { data },
  } = await axios.put(`/users/${clientId}/cleaner-restrictions`, {
    cleaner_restrictions: cleanerRestrictions,
  });

  return data;
}

export async function updateUserOneSignalEmailSubscription(
  payload: UpdateOneSignalEmailSubscriptionDto
): Promise<UpdateOneSignalEmailSubscriptionResult> {
  const {
    data: { data },
  } = await axios.patch('/users/onesignal/subscriptions/email', payload);

  return data;
}

export async function updateClientAdminReminder(
  clientId: number | string,
  ruleId: number | string,
  payload: Partial<ClientAdminReminderDto>
): Promise<ClientAdminReminder> {
  const {
    data: { data },
  } = await axios.patch(
    `/users/${clientId}/admin-reminders/${ruleId}`,
    payload
  );

  return data;
}

export const updateMultipleUsers = async (
  usersWithUpdatedRoles: UserWithUpdatedRoles[]
) => axios.post(`/users/update-multiple`, usersWithUpdatedRoles);

export async function updateClientPaymentHoldRule(
  clientId: number | string,
  payload: ClientPaymentHoldRuleDto
): Promise<ClientPaymentHoldRule> {
  const {
    data: { data },
  } = await axios.put(`/users/${clientId}/payment-hold-rule`, payload);

  const rule = normalizeClientPaymentHoldRule(data);

  if (!rule) {
    throw new Error('Payment hold rule was not returned by the API');
  }

  return rule;
}

// DELETE
export const deleteUser = async (userID: number) =>
  axios.delete(`/users/${userID}`);

export async function unlinkClientAccount(
  clientId: number | string,
  linkedUserId: number | string
): Promise<UnlinkClientAccountResult> {
  const {
    data: { data },
  } = await axios.delete(`/users/${clientId}/linked-accounts/${linkedUserId}`);

  return data;
}

export const deleteClientAdminReminder = async (
  clientId: number | string,
  ruleId: number | string
) => {
  await axios.delete(`/users/${clientId}/admin-reminders/${ruleId}`);
};

export const deleteClientPaymentHoldRule = async (
  clientId: number | string
) => {
  await axios.delete(`/users/${clientId}/payment-hold-rule`);
};

// UPLOAD IMAGE
export const uploadImage = async (formData: FormData) =>
  axios.post('/upload/public', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const resetUserPassword = async (userEmail: string) =>
  axios.post('/auth/reset-password', { email: userEmail });

export const setUserPassword = async (
  userID: number,
  newPassword: string,
  confirmPassword: string
) =>
  axios.put(`/users/${userID}/password`, {
    new_password: newPassword,
    confirm_password: confirmPassword,
  });

// GET USERS (CLIENTS) BOOKINGS
export async function getUserBookings(userId: number): Promise<Booking[]> {
  const {
    data: { data },
  } = await axios.get(`/users/${userId}/bookings`);
  return data;
}

export async function getUserTransactions(
  userId: number,
  params?: GetTransactionsParams
): Promise<TransactionsListResponse> {
  const response = await axios.get(`/users/${userId}/transactions`, {
    params,
  });

  const payload = response?.data ?? {};
  const collectionCandidates = [
    payload?.data,
    payload?.results,
    payload?.items,
    payload?.transactions,
    payload,
  ];

  const collection = collectionCandidates.find(Array.isArray) as
    | TransactionsListResponse['data']
    | undefined;

  return {
    data: collection ?? [],
    meta: payload?.meta ?? payload?.pagination ?? null,
  };
}

// GET USERS (PROVIDERS) JOBS
export async function getUserJobs(userId: number): Promise<ProviderJob[]> {
  const {
    data: { data },
  } = await axios.get(`/users/${userId}/jobs`);
  return data;
}
